/**
 * BillingDashboard - Project Management > Forecasting > Billing
 *
 * Full billing module: KPI dashboard, analytics charts, invoice management
 * (create/edit/status/delete), payment tracking, AR aging, audit trail,
 * and CSV/PDF report exports.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { exportFilename } from '../../utils/nomenclature';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, ComposedChart, Line
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Constants ────────────────────────────────────────────────────────────────

const BILLING_TYPES = [
  { value: 'lump_sum', label: 'Lump Sum' },
  { value: 'time_material', label: 'Time & Material' },
  { value: 'unit_price', label: 'Unit Price' },
  { value: 'cost_plus', label: 'Cost Plus' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'holdback_release', label: 'Holdback Release' }
];

const STATUSES = [
  { value: 'draft', label: 'Draft', color: '#6c757d', bg: '#e9ecef' },
  { value: 'submitted', label: 'Submitted', color: '#856404', bg: '#fff3cd' },
  { value: 'approved', label: 'Approved', color: '#004085', bg: '#cce5ff' },
  { value: 'sent', label: 'Sent', color: '#0c5460', bg: '#d1ecf1' },
  { value: 'partially_paid', label: 'Partially Paid', color: '#155724', bg: '#d4edda' },
  { value: 'paid', label: 'Paid', color: '#ffffff', bg: '#28a745' },
  { value: 'disputed', label: 'Disputed', color: '#721c24', bg: '#f8d7da' },
  { value: 'cancelled', label: 'Cancelled', color: '#ffffff', bg: '#6c757d' }
];

const PAYMENT_METHODS = [
  { value: 'eft', label: 'EFT' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'other', label: 'Other' }
];

const PIE_COLORS = ['#6c757d', '#ffc107', '#007bff', '#17a2b8', '#7bc96f', '#28a745', '#dc3545', '#343a40'];
const AGING_ORDER = ['current', '1-30', '31-60', '61-90', '90+'];
const AGING_COLORS = { current: '#28a745', '1-30': '#ffc107', '31-60': '#fd7e14', '61-90': '#dc3545', '90+': '#721c24' };

const fmtMoney = (v) => {
  const n = parseFloat(v);
  if (v === null || v === undefined || isNaN(n)) return '—';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtMoneyShort = (v) => {
  const n = parseFloat(v) || 0;
  if (Math.abs(n) >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  if (Math.abs(n) >= 1000) return '$' + (n / 1000).toFixed(0) + 'k';
  return '$' + n.toFixed(0);
};
const fmtDate = (d) => {
  if (!d) return '—';
  const dateOnly = String(d).slice(0, 10);
  return dateOnly;
};
const statusInfo = (s) => STATUSES.find(x => x.value === s) || STATUSES[0];
const typeLabel = (t) => (BILLING_TYPES.find(x => x.value === t) || {}).label || t;

// ─── Shared styles ────────────────────────────────────────────────────────────

const styles = {
  page: { padding: '20px', backgroundColor: '#f5f7fa', minHeight: '100%' },
  card: { backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px' },
  btn: {
    padding: '8px 14px', border: 'none', borderRadius: '6px', cursor: 'pointer',
    fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px'
  },
  input: {
    padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '6px',
    fontSize: '13px', outline: 'none', backgroundColor: '#fff'
  },
  th: {
    padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 700,
    color: '#495057', borderBottom: '2px solid #dee2e6', backgroundColor: '#f8f9fa',
    whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1
  },
  td: { padding: '9px 12px', fontSize: '13px', color: '#212529', borderBottom: '1px solid #eef0f2', whiteSpace: 'nowrap' },
  label: { display: 'block', fontSize: '12px', fontWeight: 600, color: '#495057', marginBottom: '4px' },
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
  },
  modal: {
    backgroundColor: '#fff', borderRadius: '10px', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)', width: '100%'
  }
};

const StatusBadge = ({ status }) => {
  const info = statusInfo(status);
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
      color: info.color, backgroundColor: info.bg, whiteSpace: 'nowrap'
    }}>
      {info.label}
    </span>
  );
};

const KpiCard = ({ title, value, sub, accent }) => (
  <div style={{ ...styles.card, flex: '1 1 160px', minWidth: '160px', borderTop: `3px solid ${accent}` }}>
    <div style={{ fontSize: '12px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{title}</div>
    <div style={{ fontSize: '22px', fontWeight: 700, color: '#212529', marginTop: '6px' }}>{value}</div>
    {sub && <div style={{ fontSize: '12px', color: '#868e96', marginTop: '2px' }}>{sub}</div>}
  </div>
);

// ─── Invoice form modal (create / edit) ───────────────────────────────────────

const emptyLine = () => ({ description: '', cost_code: '', quantity: 1, unit: '', unit_price: 0 });

const InvoiceFormModal = ({ invoice, onSave, onClose, saving, error }) => {
  const isEdit = !!invoice?.id;
  const [form, setForm] = useState(() => ({
    invoice_number: invoice?.invoice_number || '',
    invoice_date: invoice?.invoice_date ? fmtDate(invoice.invoice_date) : new Date().toISOString().slice(0, 10),
    due_date: invoice?.due_date ? fmtDate(invoice.due_date) : '',
    billing_type: invoice?.billing_type || 'lump_sum',
    description: invoice?.description || '',
    po_reference: invoice?.po_reference || '',
    contract_reference: invoice?.contract_reference || '',
    currency: invoice?.currency || 'CAD',
    holdback_percent: invoice?.holdback_percent ?? 10,
    tax_rate: invoice?.tax_rate ?? 0,
    notes: invoice?.notes || ''
  }));
  const [lines, setLines] = useState(() =>
    invoice?.lines?.length
      ? invoice.lines.map(l => ({
          description: l.description || '', cost_code: l.cost_code || '',
          quantity: l.quantity ?? 1, unit: l.unit || '', unit_price: l.unit_price ?? 0
        }))
      : [emptyLine()]
  );

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const setLine = (idx, key, value) => setLines(ls => ls.map((l, i) => (i === idx ? { ...l, [key]: value } : l)));

  const totals = useMemo(() => {
    const subtotal = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0);
    const holdback = subtotal * ((parseFloat(form.holdback_percent) || 0) / 100);
    const taxable = subtotal - holdback;
    const tax = taxable * ((parseFloat(form.tax_rate) || 0) / 100);
    return { subtotal, holdback, tax, total: taxable + tax };
  }, [lines, form.holdback_percent, form.tax_rate]);

  const submit = () => {
    onSave({
      ...form,
      due_date: form.due_date || null,
      lines: lines.filter(l => l.description.trim()).map(l => ({
        ...l, quantity: parseFloat(l.quantity) || 0, unit_price: parseFloat(l.unit_price) || 0
      }))
    });
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: '860px' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '17px', color: '#212529' }}>{isEdit ? `Edit Invoice ${invoice.invoice_number}` : 'New Invoice'}</h3>
          <button onClick={onClose} style={{ ...styles.btn, backgroundColor: 'transparent', fontSize: '18px', color: '#868e96' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          {error && (
            <div style={{ padding: '10px 14px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '6px', marginBottom: '14px', fontSize: '13px' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px' }}>
            <div>
              <label style={styles.label}>Invoice Number *</label>
              <input style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} value={form.invoice_number}
                     onChange={e => set('invoice_number', e.target.value)} placeholder="e.g. INV-2026-001" />
            </div>
            <div>
              <label style={styles.label}>Invoice Date</label>
              <input type="date" style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} value={form.invoice_date}
                     onChange={e => set('invoice_date', e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Due Date</label>
              <input type="date" style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} value={form.due_date}
                     onChange={e => set('due_date', e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Billing Type</label>
              <select style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} value={form.billing_type}
                      onChange={e => set('billing_type', e.target.value)}>
                {BILLING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.label}>PO Reference</label>
              <input style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} value={form.po_reference}
                     onChange={e => set('po_reference', e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Contract Reference</label>
              <input style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} value={form.contract_reference}
                     onChange={e => set('contract_reference', e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Currency</label>
              <select style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} value={form.currency}
                      onChange={e => set('currency', e.target.value)}>
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Holdback %</label>
                <input type="number" min="0" max="100" step="0.5" style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
                       value={form.holdback_percent} onChange={e => set('holdback_percent', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Tax %</label>
                <input type="number" min="0" max="100" step="0.25" style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
                       value={form.tax_rate} onChange={e => set('tax_rate', e.target.value)} />
              </div>
            </div>
          </div>
          <div style={{ marginTop: '14px' }}>
            <label style={styles.label}>Description</label>
            <input style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} value={form.description}
                   onChange={e => set('description', e.target.value)} placeholder="Billing period / scope summary" />
          </div>

          {/* Line items */}
          <div style={{ marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: 700, color: '#343a40' }}>Line Items</span>
              <button style={{ ...styles.btn, backgroundColor: '#e7f3ff', color: '#0066cc' }}
                      onClick={() => setLines(ls => [...ls, emptyLine()])}>+ Add Line</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, position: 'static' }}>#</th>
                  <th style={{ ...styles.th, position: 'static', width: '35%' }}>Description *</th>
                  <th style={{ ...styles.th, position: 'static' }}>Cost Code</th>
                  <th style={{ ...styles.th, position: 'static' }}>Qty</th>
                  <th style={{ ...styles.th, position: 'static' }}>Unit</th>
                  <th style={{ ...styles.th, position: 'static' }}>Unit Price</th>
                  <th style={{ ...styles.th, position: 'static', textAlign: 'right' }}>Amount</th>
                  <th style={{ ...styles.th, position: 'static' }}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => (
                  <tr key={idx}>
                    <td style={styles.td}>{idx + 1}</td>
                    <td style={styles.td}>
                      <input style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} value={l.description}
                             onChange={e => setLine(idx, 'description', e.target.value)} placeholder="Work performed" />
                    </td>
                    <td style={styles.td}>
                      <input style={{ ...styles.input, width: '90px' }} value={l.cost_code}
                             onChange={e => setLine(idx, 'cost_code', e.target.value)} />
                    </td>
                    <td style={styles.td}>
                      <input type="number" style={{ ...styles.input, width: '70px' }} value={l.quantity}
                             onChange={e => setLine(idx, 'quantity', e.target.value)} />
                    </td>
                    <td style={styles.td}>
                      <input style={{ ...styles.input, width: '60px' }} value={l.unit}
                             onChange={e => setLine(idx, 'unit', e.target.value)} placeholder="ea" />
                    </td>
                    <td style={styles.td}>
                      <input type="number" style={{ ...styles.input, width: '100px' }} value={l.unit_price}
                             onChange={e => setLine(idx, 'unit_price', e.target.value)} />
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>
                      {fmtMoney((parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0))}
                    </td>
                    <td style={styles.td}>
                      {lines.length > 1 && (
                        <button style={{ ...styles.btn, backgroundColor: 'transparent', color: '#dc3545', padding: '4px' }}
                                title="Remove line" onClick={() => setLines(ls => ls.filter((_, i) => i !== idx))}>🗑</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
            <div style={{ minWidth: '280px', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px 16px' }}>
              {[
                ['Subtotal', totals.subtotal],
                [`Holdback (${form.holdback_percent || 0}%)`, -totals.holdback],
                [`Tax (${form.tax_rate || 0}%)`, totals.tax]
              ].map(([label, amt]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '3px 0', color: '#495057' }}>
                  <span>{label}</span><span>{fmtMoney(amt)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 700, borderTop: '1px solid #dee2e6', marginTop: '6px', paddingTop: '6px', color: '#212529' }}>
                <span>Invoice Total</span><span>{fmtMoney(totals.total)}</span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '14px' }}>
            <label style={styles.label}>Notes</label>
            <textarea style={{ ...styles.input, width: '100%', boxSizing: 'border-box', minHeight: '60px', resize: 'vertical' }}
                      value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e9ecef', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button style={{ ...styles.btn, backgroundColor: '#e9ecef', color: '#495057' }} onClick={onClose}>Cancel</button>
          <button style={{ ...styles.btn, backgroundColor: '#0066cc', color: '#fff', opacity: saving ? 0.6 : 1 }}
                  disabled={saving} onClick={submit}>
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Invoice')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Invoice detail modal ─────────────────────────────────────────────────────

const InvoiceDetailModal = ({ invoice, onClose, onEdit, onDelete, onStatusChange, onRecordPayment, onDeletePayment, busy, error }) => {
  const [payForm, setPayForm] = useState({ payment_date: new Date().toISOString().slice(0, 10), amount: '', method: 'eft', reference_no: '', notes: '' });
  const paid = (invoice.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const balance = (parseFloat(invoice.total_amount) || 0) - paid;
  const locked = ['paid', 'cancelled'].includes(invoice.status);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: '820px' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '17px', color: '#212529' }}>Invoice {invoice.invoice_number}</h3>
            <StatusBadge status={invoice.status} />
          </div>
          <button onClick={onClose} style={{ ...styles.btn, backgroundColor: 'transparent', fontSize: '18px', color: '#868e96' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          {error && (
            <div style={{ padding: '10px 14px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '6px', marginBottom: '14px', fontSize: '13px' }}>
              {error}
            </div>
          )}
          {/* Header info */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
            {[
              ['Invoice Date', fmtDate(invoice.invoice_date)],
              ['Due Date', fmtDate(invoice.due_date)],
              ['Billing Type', typeLabel(invoice.billing_type)],
              ['Currency', invoice.currency],
              ['PO Reference', invoice.po_reference || '—'],
              ['Contract Ref', invoice.contract_reference || '—'],
              ['Created By', invoice.created_by_name || '—'],
              ['Last Updated', fmtDate(invoice.updated_at)]
            ].map(([k, v]) => (
              <div key={k}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#868e96', textTransform: 'uppercase' }}>{k}</div>
                <div style={{ fontSize: '13px', color: '#212529', marginTop: '2px' }}>{v}</div>
              </div>
            ))}
          </div>
          {invoice.description && (
            <div style={{ fontSize: '13px', color: '#495057', backgroundColor: '#f8f9fa', padding: '10px 12px', borderRadius: '6px', marginBottom: '16px' }}>
              {invoice.description}
            </div>
          )}

          {/* Lines */}
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#343a40', marginBottom: '6px' }}>Line Items</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px' }}>
            <thead>
              <tr>
                {['#', 'Description', 'Cost Code', 'Qty', 'Unit', 'Unit Price', 'Amount'].map((h, i) => (
                  <th key={h} style={{ ...styles.th, position: 'static', textAlign: i >= 5 ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(invoice.lines || []).map(l => (
                <tr key={l.id}>
                  <td style={styles.td}>{l.line_no}</td>
                  <td style={{ ...styles.td, whiteSpace: 'normal' }}>{l.description}</td>
                  <td style={styles.td}>{l.cost_code || '—'}</td>
                  <td style={styles.td}>{parseFloat(l.quantity)}</td>
                  <td style={styles.td}>{l.unit || '—'}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{fmtMoney(l.unit_price)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '18px' }}>
            <div style={{ minWidth: '300px', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px 16px' }}>
              {[
                ['Subtotal', invoice.subtotal],
                [`Holdback (${parseFloat(invoice.holdback_percent) || 0}%)`, -(parseFloat(invoice.holdback_amount) || 0)],
                [`Tax (${parseFloat(invoice.tax_rate) || 0}%)`, invoice.tax_amount],
                ['Invoice Total', invoice.total_amount],
                ['Paid to Date', paid],
                ['Balance', balance]
              ].map(([label, amt], i) => (
                <div key={label} style={{
                  display: 'flex', justifyContent: 'space-between', fontSize: i >= 3 ? '14px' : '13px',
                  fontWeight: i >= 3 ? 700 : 400, padding: '3px 0',
                  color: label === 'Balance' ? (balance > 0.005 ? '#dc3545' : '#28a745') : '#343a40',
                  borderTop: i === 3 ? '1px solid #dee2e6' : 'none'
                }}>
                  <span>{label}</span><span>{fmtMoney(amt)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Payments */}
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#343a40', marginBottom: '6px' }}>
            Payments ({(invoice.payments || []).length})
          </div>
          {(invoice.payments || []).length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
              <thead>
                <tr>
                  {['Date', 'Amount', 'Method', 'Reference', 'Recorded By', ''].map(h => (
                    <th key={h} style={{ ...styles.th, position: 'static' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoice.payments.map(p => (
                  <tr key={p.id}>
                    <td style={styles.td}>{fmtDate(p.payment_date)}</td>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{fmtMoney(p.amount)}</td>
                    <td style={styles.td}>{(PAYMENT_METHODS.find(m => m.value === p.method) || {}).label || p.method}</td>
                    <td style={styles.td}>{p.reference_no || '—'}</td>
                    <td style={styles.td}>{p.recorded_by_name || '—'}</td>
                    <td style={styles.td}>
                      <button style={{ ...styles.btn, backgroundColor: 'transparent', color: '#dc3545', padding: '2px 6px' }}
                              title="Delete payment"
                              onClick={() => window.confirm('Remove this payment?') && onDeletePayment(p.id)}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: '13px', color: '#868e96', marginBottom: '12px' }}>No payments recorded yet.</div>
          )}

          {/* Record payment */}
          {invoice.status !== 'cancelled' && balance > 0.005 && (
            <div style={{ backgroundColor: '#f0f7ff', borderRadius: '8px', padding: '12px 14px', marginBottom: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#004085', marginBottom: '8px' }}>Record Payment</div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <label style={styles.label}>Date</label>
                  <input type="date" style={styles.input} value={payForm.payment_date}
                         onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} />
                </div>
                <div>
                  <label style={styles.label}>Amount</label>
                  <input type="number" min="0" step="0.01" style={{ ...styles.input, width: '120px' }} value={payForm.amount}
                         placeholder={balance.toFixed(2)}
                         onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} />
                </div>
                <div>
                  <label style={styles.label}>Method</label>
                  <select style={styles.input} value={payForm.method}
                          onChange={e => setPayForm(f => ({ ...f, method: e.target.value }))}>
                    {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Reference #</label>
                  <input style={{ ...styles.input, width: '130px' }} value={payForm.reference_no}
                         onChange={e => setPayForm(f => ({ ...f, reference_no: e.target.value }))} />
                </div>
                <button style={{ ...styles.btn, backgroundColor: '#28a745', color: '#fff', opacity: busy ? 0.6 : 1 }}
                        disabled={busy}
                        onClick={() => onRecordPayment(payForm, () => setPayForm(f => ({ ...f, amount: '', reference_no: '' })))}>
                  💰 Record
                </button>
                <button style={{ ...styles.btn, backgroundColor: '#e7f3ff', color: '#0066cc' }}
                        onClick={() => setPayForm(f => ({ ...f, amount: balance.toFixed(2) }))}>
                  Pay Full Balance
                </button>
              </div>
            </div>
          )}
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#495057' }}>Status:</span>
            <select style={styles.input} value={invoice.status} disabled={busy}
                    onChange={e => onStatusChange(e.target.value)}>
              {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={{ ...styles.btn, backgroundColor: '#f8d7da', color: '#721c24' }}
                    onClick={() => window.confirm(`Delete invoice ${invoice.invoice_number}? It will be removed from all totals.`) && onDelete()}>
              🗑 Delete
            </button>
            {!locked && (
              <button style={{ ...styles.btn, backgroundColor: '#0066cc', color: '#fff' }} onClick={onEdit}>✏️ Edit</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Billing Details tab (SOV-style progress billing worksheet) ───────────────

const DetailsCellInput = ({ value, onSave, type = 'number', width = 110, align = 'right', placeholder }) => {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  return (
    <input
      type={type}
      style={{ ...styles.input, width: `${width}px`, textAlign: align, padding: '5px 8px' }}
      value={draft}
      placeholder={placeholder}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => { if (String(draft) !== String(value ?? '')) onSave(draft); }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
    />
  );
};

const BillingDetailsTab = ({ details, onUpdateRow, onAddRow, onDeleteRow, onSeed, busy }) => {
  const rows = useMemo(() => details?.rows || [], [details]);
  const disciplines = details?.disciplines || [];
  const trackerProgress = details?.trackerProgress || {};
  const invoiced = details?.invoiced || {};

  const totals = useMemo(() => {
    const t = rows.reduce((acc, r) => ({
      scheduled: acc.scheduled + (parseFloat(r.scheduled_value) || 0),
      earned: acc.earned + (parseFloat(r.earned_value) || 0),
      billed: acc.billed + (parseFloat(r.billed_to_date) || 0),
      remaining: acc.remaining + (parseFloat(r.remaining_to_bill) || 0)
    }), { scheduled: 0, earned: 0, billed: 0, remaining: 0 });
    t.pct = t.scheduled > 0 ? (t.earned / t.scheduled) * 100 : 0;
    t.overUnder = t.billed - t.earned;
    return t;
  }, [rows]);

  const chartData = useMemo(() =>
    rows.filter(r => (parseFloat(r.scheduled_value) || 0) > 0).map(r => ({
      name: r.description.length > 18 ? r.description.slice(0, 17) + '…' : r.description,
      Scheduled: parseFloat(r.scheduled_value) || 0,
      Earned: parseFloat(r.earned_value) || 0,
      Billed: parseFloat(r.billed_to_date) || 0
    })), [rows]);

  const reconcileDelta = totals.billed - (invoiced.total_invoiced || 0);

  return (
    <>
      {/* Worksheet KPI strip */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <KpiCard title="Scheduled Value" value={fmtMoney(totals.scheduled)} sub={`${rows.length} line${rows.length === 1 ? '' : 's'}`} accent="#0066cc" />
        <KpiCard title="Physical Progress" value={`${totals.pct.toFixed(1)}%`} sub="Weighted by scheduled value" accent="#17a2b8" />
        <KpiCard title="Earned to Date" value={fmtMoney(totals.earned)} sub="Scheduled × physical %" accent="#28a745" />
        <KpiCard title="Billed to Date" value={fmtMoney(totals.billed)}
                 sub={Math.abs(reconcileDelta) > 0.01 ? `${fmtMoney(Math.abs(reconcileDelta))} ${reconcileDelta > 0 ? 'above' : 'below'} invoiced` : 'Matches invoiced total'}
                 accent="#6f42c1" />
        <KpiCard title={totals.overUnder >= 0 ? 'Overbilled' : 'Underbilled'}
                 value={fmtMoney(Math.abs(totals.overUnder))}
                 sub="Billed − earned" accent={totals.overUnder >= 0 ? '#007bff' : '#dc3545'} />
        <KpiCard title="Remaining to Bill" value={fmtMoney(totals.remaining)} sub="Scheduled − billed" accent="#ffc107" />
      </div>

      {/* Worksheet */}
      <div style={{ ...styles.card, marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#343a40' }}>Progress Billing Worksheet</span>
            <span style={{ fontSize: '12px', color: '#868e96', marginLeft: '10px' }}>
              Physical % pulls live from the progress trackers (hours-weighted); type a value to override it.
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...styles.btn, backgroundColor: '#e7f3ff', color: '#0066cc', opacity: busy ? 0.6 : 1 }} disabled={busy}
                    title="Create one line per discipline that has tracker data" onClick={onSeed}>
              ⚙ Load from Trackers
            </button>
            <button style={{ ...styles.btn, backgroundColor: '#28a745', color: '#fff', opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={onAddRow}>
              + Add Line
            </button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['#', 'Description', 'Discipline (tracker)', 'Scheduled Value', 'Physical %', 'Earned Value', 'Billed to Date', 'Remaining', 'Over / (Under)', 'Notes', ''].map((h, i) => (
                  <th key={h || 'x'} style={{ ...styles.th, position: 'static', textAlign: i >= 3 && i <= 8 ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ ...styles.td, textAlign: 'center', color: '#868e96', padding: '36px' }}>
                    No lines yet. Use “⚙ Load from Trackers” to seed a line per discipline, or “+ Add Line”.
                  </td>
                </tr>
              )}
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={styles.td}>{r.line_no}</td>
                  <td style={styles.td}>
                    <DetailsCellInput type="text" width={190} align="left" value={r.description}
                                      onSave={v => onUpdateRow(r.id, { description: v })} />
                  </td>
                  <td style={styles.td}>
                    <select style={{ ...styles.input, padding: '5px 8px' }} value={r.discipline || ''}
                            onChange={e => onUpdateRow(r.id, { discipline: e.target.value })}>
                      <option value="">— manual —</option>
                      {disciplines.map(d => (
                        <option key={d.key} value={d.key}>
                          {d.label}{trackerProgress[d.key] ? ` (${trackerProgress[d.key].pct.toFixed(1)}%)` : ' (no data)'}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <DetailsCellInput value={r.scheduled_value} onSave={v => onUpdateRow(r.id, { scheduled_value: v })} />
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                      <div style={{ width: '52px', height: '6px', backgroundColor: '#e9ecef', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, r.physical_pct || 0)}%`, height: '100%', backgroundColor: r.progress_source === 'manual' ? '#fd7e14' : '#28a745' }} />
                      </div>
                      <DetailsCellInput width={64} value={r.progress_override ?? ''}
                                        placeholder={r.tracker_pct !== null && r.tracker_pct !== undefined ? Number(r.tracker_pct).toFixed(1) : '0'}
                                        onSave={v => onUpdateRow(r.id, { progress_override: v === '' ? null : v })} />
                      <span style={{
                        fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '8px',
                        backgroundColor: r.progress_source === 'tracker' ? '#d4edda' : r.progress_source === 'manual' ? '#ffe8d1' : '#e9ecef',
                        color: r.progress_source === 'tracker' ? '#155724' : r.progress_source === 'manual' ? '#8a4b08' : '#6c757d'
                      }} title={r.progress_source === 'tracker' ? 'Live from progress tracker' : r.progress_source === 'manual' ? 'Manual override (clear the box to go back to tracker)' : 'No tracker linked'}>
                        {r.progress_source === 'tracker' ? 'AUTO' : r.progress_source === 'manual' ? 'MAN' : '—'}
                      </span>
                    </div>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(r.earned_value)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <DetailsCellInput value={r.billed_to_date} onSave={v => onUpdateRow(r.id, { billed_to_date: v })} />
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: parseFloat(r.remaining_to_bill) > 0.005 ? '#212529' : '#28a745' }}>
                    {fmtMoney(r.remaining_to_bill)}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: parseFloat(r.over_under) >= 0 ? '#007bff' : '#dc3545' }}>
                    {parseFloat(r.over_under) < 0 ? `(${fmtMoney(Math.abs(r.over_under))})` : fmtMoney(r.over_under)}
                  </td>
                  <td style={styles.td}>
                    <DetailsCellInput type="text" width={140} align="left" value={r.notes || ''}
                                      onSave={v => onUpdateRow(r.id, { notes: v })} />
                  </td>
                  <td style={styles.td}>
                    <button style={{ ...styles.btn, backgroundColor: 'transparent', color: '#dc3545', padding: '2px 6px' }}
                            title="Delete line"
                            onClick={() => window.confirm(`Delete line "${r.description}"?`) && onDeleteRow(r.id)}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ ...styles.td, fontWeight: 700, borderTop: '2px solid #dee2e6' }}>Total</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, borderTop: '2px solid #dee2e6' }}>{fmtMoney(totals.scheduled)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, borderTop: '2px solid #dee2e6' }}>{totals.pct.toFixed(1)}%</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, borderTop: '2px solid #dee2e6' }}>{fmtMoney(totals.earned)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, borderTop: '2px solid #dee2e6' }}>{fmtMoney(totals.billed)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, borderTop: '2px solid #dee2e6' }}>{fmtMoney(totals.remaining)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, borderTop: '2px solid #dee2e6', color: totals.overUnder >= 0 ? '#007bff' : '#dc3545' }}>
                    {totals.overUnder < 0 ? `(${fmtMoney(Math.abs(totals.overUnder))})` : fmtMoney(totals.overUnder)}
                  </td>
                  <td colSpan={2} style={{ ...styles.td, borderTop: '2px solid #dee2e6' }}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Earned vs Billed chart */}
      {chartData.length > 0 && (
        <div style={styles.card}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#343a40', marginBottom: '10px' }}>Scheduled vs Earned vs Billed by Line</div>
          <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 46)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
              <XAxis type="number" tickFormatter={fmtMoneyShort} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Bar dataKey="Scheduled" fill="#adb5bd" radius={[0, 3, 3, 0]} barSize={10} />
              <Bar dataKey="Earned" fill="#28a745" radius={[0, 3, 3, 0]} barSize={10} />
              <Bar dataKey="Billed" fill="#0066cc" radius={[0, 3, 3, 0]} barSize={10} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const BillingDashboard = ({ projectId, projectDetails }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('billing', projectDetails?.job_number || projectId || 'global', true);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [details, setDetails] = useState(null);
  const [detailsBusy, setDetailsBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Filters
  const [filters, setFilters] = useState({ search: '', status: '', billing_type: '', from: '', to: '' });

  // Modals
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [modalBusy, setModalBusy] = useState(false);
  const [modalError, setModalError] = useState(null);

  const api = useCallback(
    (method, url, data) => authenticatedApiCall(instance, accounts, method, url, data),
    [instance, accounts]
  );

  const fetchAll = useCallback(async () => {
    if (!projectId) return;
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const qs = params.toString() ? `?${params.toString()}` : '';
      const [summaryRes, invoicesRes, auditRes, detailsRes] = await Promise.all([
        api('GET', `/billing/${projectId}/summary`),
        api('GET', `/billing/${projectId}/invoices${qs}`),
        api('GET', `/billing/${projectId}/audit?limit=50`),
        api('GET', `/billing/${projectId}/details`)
      ]);
      if (summaryRes.data.success) setSummary(summaryRes.data.data);
      if (invoicesRes.data.success) setInvoices(invoicesRes.data.data);
      if (auditRes.data.success) setAuditLog(auditRes.data.data);
      if (detailsRes.data.success) setDetails(detailsRes.data.data);
    } catch (err) {
      console.error('Error loading billing data:', err);
      setLoadError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [api, projectId, filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refreshDetail = useCallback(async (invoiceId) => {
    const res = await api('GET', `/billing/${projectId}/invoices/${invoiceId}`);
    if (res.data.success) setDetailInvoice(res.data.data);
  }, [api, projectId]);

  // ── Actions ──
  const handleSaveInvoice = async (payload) => {
    setModalBusy(true); setModalError(null);
    try {
      if (editingInvoice?.id) {
        await api('PUT', `/billing/${projectId}/invoices/${editingInvoice.id}`, payload);
      } else {
        await api('POST', `/billing/${projectId}/invoices`, payload);
      }
      setShowForm(false); setEditingInvoice(null);
      await fetchAll();
      if (detailInvoice) await refreshDetail(detailInvoice.id);
    } catch (err) {
      setModalError(err.response?.data?.error || err.message || 'Failed to save invoice');
    } finally {
      setModalBusy(false);
    }
  };

  const handleStatusChange = async (status) => {
    setModalBusy(true); setModalError(null);
    try {
      await api('PATCH', `/billing/${projectId}/invoices/${detailInvoice.id}/status`, { status });
      await refreshDetail(detailInvoice.id);
      await fetchAll();
    } catch (err) {
      setModalError(err.response?.data?.error || err.message || 'Failed to update status');
    } finally {
      setModalBusy(false);
    }
  };

  const handleDeleteInvoice = async () => {
    setModalBusy(true); setModalError(null);
    try {
      await api('DELETE', `/billing/${projectId}/invoices/${detailInvoice.id}`);
      setDetailInvoice(null);
      await fetchAll();
    } catch (err) {
      setModalError(err.response?.data?.error || err.message || 'Failed to delete invoice');
    } finally {
      setModalBusy(false);
    }
  };

  const handleRecordPayment = async (payForm, resetForm) => {
    setModalBusy(true); setModalError(null);
    try {
      await api('POST', `/billing/${projectId}/invoices/${detailInvoice.id}/payments`, payForm);
      resetForm();
      await refreshDetail(detailInvoice.id);
      await fetchAll();
    } catch (err) {
      setModalError(err.response?.data?.error || err.message || 'Failed to record payment');
    } finally {
      setModalBusy(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    setModalBusy(true); setModalError(null);
    try {
      await api('DELETE', `/billing/${projectId}/invoices/${detailInvoice.id}/payments/${paymentId}`);
      await refreshDetail(detailInvoice.id);
      await fetchAll();
    } catch (err) {
      setModalError(err.response?.data?.error || err.message || 'Failed to delete payment');
    } finally {
      setModalBusy(false);
    }
  };

  // ── Billing Details actions ──
  const refreshDetails = useCallback(async () => {
    const res = await api('GET', `/billing/${projectId}/details`);
    if (res.data.success) setDetails(res.data.data);
  }, [api, projectId]);

  const handleUpdateDetailRow = async (rowId, patch) => {
    setDetailsBusy(true);
    try {
      await api('PUT', `/billing/${projectId}/details/${rowId}`, patch);
      await refreshDetails();
    } catch (err) {
      window.alert(err.response?.data?.error || err.message || 'Failed to update line');
    } finally {
      setDetailsBusy(false);
    }
  };

  const handleAddDetailRow = async () => {
    setDetailsBusy(true);
    try {
      await api('POST', `/billing/${projectId}/details`, { description: 'New line' });
      await refreshDetails();
    } catch (err) {
      window.alert(err.response?.data?.error || err.message || 'Failed to add line');
    } finally {
      setDetailsBusy(false);
    }
  };

  const handleDeleteDetailRow = async (rowId) => {
    setDetailsBusy(true);
    try {
      await api('DELETE', `/billing/${projectId}/details/${rowId}`);
      await refreshDetails();
    } catch (err) {
      window.alert(err.response?.data?.error || err.message || 'Failed to delete line');
    } finally {
      setDetailsBusy(false);
    }
  };

  const handleSeedDetails = async () => {
    setDetailsBusy(true);
    try {
      const res = await api('POST', `/billing/${projectId}/details/seed`);
      await refreshDetails();
      if (res.data.data?.length === 0) window.alert('No new lines — every discipline with tracker data already has a line.');
    } catch (err) {
      window.alert(err.response?.data?.error || err.message || 'Failed to load from trackers');
    } finally {
      setDetailsBusy(false);
    }
  };

  // ── Reports ──
  const exportCsv = () => {
    const header = ['Invoice #', 'Date', 'Due Date', 'Type', 'Status', 'Description', 'PO Ref', 'Currency', 'Subtotal', 'Holdback', 'Tax', 'Total', 'Paid', 'Balance'];
    const rows = invoices.map(i => [
      i.invoice_number, fmtDate(i.invoice_date), fmtDate(i.due_date), typeLabel(i.billing_type),
      statusInfo(i.status).label, (i.description || '').replace(/"/g, '""'), i.po_reference || '',
      i.currency, i.subtotal, i.holdback_amount, i.tax_amount, i.total_amount, i.paid_amount, i.balance
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${c ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `billing_${projectDetails?.job_number || projectId}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const exportPdf = async () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const jobNumber = projectDetails?.job_number;
    const { y: letterheadY } = await applyLetterhead(doc, {
      instance, accounts, jobNumber, projectId,
      jobName: projectDetails?.job_name,
      margin: 14,
    });
    const jobLabel = `${projectDetails?.job_number || ''} ${projectDetails?.job_name || ''}`.trim();
    doc.setFontSize(16);
    doc.text('Billing Report', 14, letterheadY);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Project: ${jobLabel || projectId}   Generated: ${new Date().toLocaleString()}`, 14, letterheadY + 7);

    if (summary?.totals) {
      const t = summary.totals;
      autoTable(doc, {
        startY: letterheadY + 12,
        head: [['Invoices', 'Total Billed', 'Collected', 'Outstanding', 'Overdue', 'Holdback']],
        body: [[
          t.invoice_count, fmtMoney(t.total_billed), fmtMoney(t.total_collected),
          fmtMoney(t.outstanding), fmtMoney(t.overdue), fmtMoney(t.total_holdback)
        ]],
        theme: 'grid',
        headStyles: { fillColor: [0, 102, 204] }
      });
    }

    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || 28) + 8,
      head: [['Invoice #', 'Date', 'Due', 'Type', 'Status', 'Total', 'Paid', 'Balance']],
      body: invoices.map(i => [
        i.invoice_number, fmtDate(i.invoice_date), fmtDate(i.due_date), typeLabel(i.billing_type),
        statusInfo(i.status).label, fmtMoney(i.total_amount), fmtMoney(i.paid_amount), fmtMoney(i.balance)
      ]),
      theme: 'striped',
      headStyles: { fillColor: [52, 58, 64] },
      styles: { fontSize: 8 }
    });

    doc.save(exportFilename({ job: projectDetails?.job_number || projectId, reportSlug: 'Billing_Report', ext: 'pdf' }));
  };

  // ── Chart data ──
  const monthlyData = useMemo(() =>
    (summary?.monthly || []).map(m => ({
      month: m.month,
      Billed: parseFloat(m.billed) || 0,
      Collected: parseFloat(m.collected) || 0
    })), [summary]);

  const statusData = useMemo(() =>
    (summary?.byStatus || []).map(s => ({
      name: statusInfo(s.status).label,
      value: parseFloat(s.amount) || 0,
      count: s.count
    })).filter(s => s.value > 0), [summary]);

  const agingData = useMemo(() => {
    const map = Object.fromEntries((summary?.aging || []).map(a => [a.bucket, a]));
    return AGING_ORDER.map(bucket => ({
      bucket: bucket === 'current' ? 'Current' : `${bucket} days`,
      key: bucket,
      Amount: parseFloat(map[bucket]?.amount) || 0,
      count: map[bucket]?.count || 0
    }));
  }, [summary]);

  const totals = summary?.totals || {};
  const collectionRate = (parseFloat(totals.total_billed) || 0) > 0
    ? ((parseFloat(totals.total_collected) || 0) / parseFloat(totals.total_billed)) * 100
    : 0;

  if (loading) {
    return (
      <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
        <span style={{ color: '#868e96', fontSize: '14px' }}>Loading billing data…</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.card, borderLeft: '4px solid #dc3545' }}>
          <div style={{ fontWeight: 700, color: '#721c24', marginBottom: '6px' }}>Unable to load Billing</div>
          <div style={{ fontSize: '13px', color: '#495057' }}>{loadError}</div>
          <button style={{ ...styles.btn, backgroundColor: '#0066cc', color: '#fff', marginTop: '12px' }}
                  onClick={() => { setLoading(true); fetchAll(); }}>Retry</button>
        </div>
      </div>
    );
  }

  const tabBtn = (id, label) => (
    <button key={id} onClick={() => setActiveTab(id)} style={{
      ...styles.btn,
      backgroundColor: activeTab === id ? '#0066cc' : 'transparent',
      color: activeTab === id ? '#fff' : '#495057',
      border: activeTab === id ? 'none' : '1px solid #ced4da'
    }}>{label}</button>
  );

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#212529' }}>💵 Billing</h2>
          <div style={{ fontSize: '13px', color: '#868e96', marginTop: '2px' }}>
            Invoices, payments and receivables for {projectDetails?.job_number ? `${projectDetails.job_number} — ${projectDetails.job_name || ''}` : 'this project'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          {tabBtn('dashboard', '📊 Dashboard')}
          {tabBtn('details', '📐 Billing Details')}
          {tabBtn('invoices', `🧾 Invoices (${invoices.length})`)}
          {tabBtn('audit', '📜 Audit Trail')}
          <button style={{ ...styles.btn, backgroundColor: '#e9ecef', color: '#495057' }} onClick={exportCsv} title="Export invoice list to CSV">⬇ CSV</button>
          <button style={{ ...styles.btn, backgroundColor: '#e9ecef', color: '#495057' }} onClick={exportPdf} title="Export billing report PDF">⬇ PDF</button>
          <button style={{ ...styles.btn, backgroundColor: '#28a745', color: '#fff' }}
                  onClick={() => { setEditingInvoice(null); setModalError(null); setShowForm(true); }}>
            + New Invoice
          </button>
        </div>
      </div>

      {/* KPI cards (invoice-based; the Billing Details tab has its own strip) */}
      {activeTab !== 'details' && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
          <KpiCard title="Total Billed" value={fmtMoney(totals.total_billed)} sub={`${totals.invoice_count || 0} invoice${totals.invoice_count === 1 ? '' : 's'}`} accent="#0066cc" />
          <KpiCard title="Collected" value={fmtMoney(totals.total_collected)} sub={`${collectionRate.toFixed(1)}% collection rate`} accent="#28a745" />
          <KpiCard title="Outstanding" value={fmtMoney(totals.outstanding)} sub="Unpaid balance" accent="#ffc107" />
          <KpiCard title="Overdue" value={fmtMoney(totals.overdue)} sub={`${totals.overdue_count || 0} invoice${totals.overdue_count === 1 ? '' : 's'} past due`} accent="#dc3545" />
          <KpiCard title="Holdback" value={fmtMoney(totals.total_holdback)} sub="Retained by client" accent="#6f42c1" />
        </div>
      )}

      {/* ── Billing Details tab ── */}
      {activeTab === 'details' && (
        <BillingDetailsTab
          details={details}
          busy={detailsBusy}
          onUpdateRow={handleUpdateDetailRow}
          onAddRow={handleAddDetailRow}
          onDeleteRow={handleDeleteDetailRow}
          onSeed={handleSeedDetails}
        />
      )}

      {/* ── Dashboard tab ── */}
      {activeTab === 'dashboard' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#343a40', marginBottom: '10px' }}>Billed vs Collected by Month</div>
              {monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => fmtMoney(v)} />
                    <Legend />
                    <Bar dataKey="Billed" fill="#0066cc" radius={[3, 3, 0, 0]} />
                    <Line dataKey="Collected" stroke="#28a745" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: '#868e96', fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>No invoices yet — create your first invoice to see trends.</div>
              )}
            </div>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#343a40', marginBottom: '10px' }}>Amount by Status</div>
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                      {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtMoney(v)} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: '#868e96', fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>No data</div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#343a40', marginBottom: '10px' }}>Receivables Aging</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={agingData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="Amount" radius={[3, 3, 0, 0]}>
                    {agingData.map((entry) => <Cell key={entry.key} fill={AGING_COLORS[entry.key]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#343a40', marginBottom: '10px' }}>Billing by Type</div>
              {(summary?.byType || []).length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, position: 'static' }}>Type</th>
                      <th style={{ ...styles.th, position: 'static' }}>Invoices</th>
                      <th style={{ ...styles.th, position: 'static', textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.byType.map(t => (
                      <tr key={t.billing_type}>
                        <td style={styles.td}>{typeLabel(t.billing_type)}</td>
                        <td style={styles.td}>{t.count}</td>
                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(t.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ color: '#868e96', fontSize: '13px', padding: '30px 0', textAlign: 'center' }}>No data</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Invoices tab ── */}
      {activeTab === 'invoices' && (
        <div style={styles.card}>
          {/* Filter bar */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px', alignItems: 'flex-end' }}>
            <div>
              <label style={styles.label}>Search</label>
              <input style={{ ...styles.input, width: '200px' }} placeholder="Invoice #, description, PO…"
                     value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
            </div>
            <div>
              <label style={styles.label}>Status</label>
              <select style={styles.input} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="">All</option>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.label}>Type</label>
              <select style={styles.input} value={filters.billing_type} onChange={e => setFilters(f => ({ ...f, billing_type: e.target.value }))}>
                <option value="">All</option>
                {BILLING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.label}>From</label>
              <input type="date" style={styles.input} value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
            </div>
            <div>
              <label style={styles.label}>To</label>
              <input type="date" style={styles.input} value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
            </div>
            {(filters.search || filters.status || filters.billing_type || filters.from || filters.to) && (
              <button style={{ ...styles.btn, backgroundColor: '#e9ecef', color: '#495057' }}
                      onClick={() => setFilters({ search: '', status: '', billing_type: '', from: '', to: '' })}>
                Clear Filters
              </button>
            )}
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto', maxHeight: '540px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Invoice #', 'Date', 'Due', 'Type', 'Status', 'Lines', 'Total', 'Paid', 'Balance', 'Created By'].map((h, i) => (
                    <th key={h} style={{ ...styles.th, textAlign: i >= 6 && i <= 8 ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ ...styles.td, textAlign: 'center', color: '#868e96', padding: '40px' }}>
                      No invoices found. Click “+ New Invoice” to create one.
                    </td>
                  </tr>
                )}
                {invoices.map(inv => (
                  <tr key={inv.id}
                      style={{ cursor: 'pointer', backgroundColor: inv.is_overdue ? '#fff5f5' : 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = inv.is_overdue ? '#fff5f5' : 'transparent'}
                      onClick={async () => { setModalError(null); await refreshDetail(inv.id); }}>
                    <td style={{ ...styles.td, fontWeight: 700, color: '#0066cc' }}>{inv.invoice_number}</td>
                    <td style={styles.td}>{fmtDate(inv.invoice_date)}</td>
                    <td style={{ ...styles.td, color: inv.is_overdue ? '#dc3545' : '#212529', fontWeight: inv.is_overdue ? 700 : 400 }}>
                      {fmtDate(inv.due_date)}{inv.is_overdue ? ' ⚠' : ''}
                    </td>
                    <td style={styles.td}>{typeLabel(inv.billing_type)}</td>
                    <td style={styles.td}><StatusBadge status={inv.status} /></td>
                    <td style={styles.td}>{inv.line_count}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(inv.total_amount)}</td>
                    <td style={{ ...styles.td, textAlign: 'right', color: '#28a745' }}>{fmtMoney(inv.paid_amount)}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: parseFloat(inv.balance) > 0.005 ? '#dc3545' : '#28a745' }}>
                      {fmtMoney(inv.balance)}
                    </td>
                    <td style={styles.td}>{inv.created_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Audit tab ── */}
      {activeTab === 'audit' && (
        <div style={styles.card}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#343a40', marginBottom: '10px' }}>Recent Billing Activity</div>
          {auditLog.length === 0 ? (
            <div style={{ color: '#868e96', fontSize: '13px', padding: '30px 0', textAlign: 'center' }}>No activity yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['When', 'Action', 'Invoice', 'Details', 'By'].map(h => (
                    <th key={h} style={{ ...styles.th, position: 'static' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {auditLog.map(a => (
                  <tr key={a.id}>
                    <td style={styles.td}>{new Date(a.performed_at).toLocaleString()}</td>
                    <td style={styles.td}>
                      <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, backgroundColor: '#e7f3ff', color: '#0066cc' }}>
                        {a.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{a.invoice_number || '—'}</td>
                    <td style={{ ...styles.td, whiteSpace: 'normal', color: '#495057', fontSize: '12px' }}>
                      {a.details ? Object.entries(a.details).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ') : '—'}
                    </td>
                    <td style={styles.td}>{a.performed_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <InvoiceFormModal
          invoice={editingInvoice}
          saving={modalBusy}
          error={modalError}
          onClose={() => { setShowForm(false); setEditingInvoice(null); setModalError(null); }}
          onSave={handleSaveInvoice}
        />
      )}
      {detailInvoice && !showForm && (
        <InvoiceDetailModal
          invoice={detailInvoice}
          busy={modalBusy}
          error={modalError}
          onClose={() => { setDetailInvoice(null); setModalError(null); }}
          onEdit={() => { setEditingInvoice(detailInvoice); setModalError(null); setShowForm(true); }}
          onDelete={handleDeleteInvoice}
          onStatusChange={handleStatusChange}
          onRecordPayment={handleRecordPayment}
          onDeletePayment={handleDeletePayment}
        />
      )}
    </div>
  );
};

export default BillingDashboard;
